/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Grid,
  MenuItem,
  InputLabel,
  Divider,
  Typography,
  Button,
} from "@material-ui/core";
import { isAfter, parseISO } from "date-fns";

import StyledTextField from "../common/StyledTextField";
import StickyButton from "../common/StickyButton";
import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";

import types from "../../helpers/types";
import { formatUserLocalTimeWithYear } from "../../helpers/dateHelpers";
import {
  postUserInvite,
  putUsersAssociatedCarriers,
  putUserUpdate,
} from "../../api/api";
import { fetchUsers } from "../../slices/userManagementSlice";
import AutoCompleteMultiSelect from "../common/AutoCompleteMultiSelect";
import { isCarrier } from "../../helpers/goTrackUserUtils";
import ActionsModal from "../common/ActionsModal";

function UserUpdateForm({
  setShowUserUpdateSuccessNotification,
  setShowInviteResentSuccessNotification,
  setUnsavedChanges,
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userRole, setUserRole] = useState("");
  const [accountEnabled, setAccountEnabled] = useState(true);
  const [userUpdateRequestStatus, setUserUpdateRequestStatus] = useState(
    types.requestStatuses.initial
  );
  const [associatedCarrierList, setAssociatedCarrierList] = useState([]);
  const [allCarriers, setAllCarriers] = useState([]);
  const [isMissingCarrierAssociation, setIsMissingCarrierAssociation] =
    useState(false);
  const isFetchingUsers = useSelector(
    (state) => state.userManagementUsers.isFetching
  );
  const currentPage = useSelector(
    (state) => state.userManagementFilters.currentPage
  );
  const carriers = useSelector((state) => state.carriers.carriers);
  const user = useSelector((state) => state.user.user);
  const [inviteDateUtc, setInviteDateUtc] = useState(user.inviteDateUtc);
  const [resendUserInviteRequestStatus, setResendUserInviteRequestStatus] =
    useState(types.requestStatuses.initial);
  const [showAccountStatusChangeModal, setShowAccountStatusChangeModal] =
    useState(false);

  const dispatch = useDispatch();

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
    setUnsavedChanges(true);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
    setUnsavedChanges(true);
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
    setUnsavedChanges(true);
  };

  const handleSelectedUserType = (event) => {
    setUserRole(event.target.value);
    setUnsavedChanges(true);
  };

  const handleAssociatedCarriersChange = (associatedCarriers) => {
    setAssociatedCarrierList(associatedCarriers);
    setUnsavedChanges(true);
  };

  const handleAccountEnabledChange = (event) => {
    setAccountEnabled(event.target.value);
    setUnsavedChanges(true);
  };

  const saveChanges = async () => {
    const updates = {
      firstName,
      lastName,
      phone: phoneNumber,
      role: userRole,
      accountEnabled,
    };
    const isCarrierUser = isCarrier([userRole]);

    if (isCarrier([userRole]) && associatedCarrierList.length === 0) {
      setIsMissingCarrierAssociation(true);
    } else {
      try {
        let requests = [putUserUpdate(user.usersId, updates)];
        if (isCarrierUser) {
          const carrierIds = associatedCarrierList.map((carrier) => carrier.id);
          requests.push(putUsersAssociatedCarriers(user.usersId, carrierIds));
        }
        setUserUpdateRequestStatus(types.requestStatuses.inProgress);
        await Promise.all(requests);
        setUserUpdateRequestStatus(types.requestStatuses.success);
        setShowUserUpdateSuccessNotification(true);
        dispatch(fetchUsers(currentPage));
        setUnsavedChanges(false);
      } catch {
        setUserUpdateRequestStatus(types.requestStatuses.failure);
      }
    }
  };

  const handleSaveButtonClick = () => {
    if (accountEnabled !== user.accountEnabled) {
      setShowAccountStatusChangeModal(true);
      return;
    }

    saveChanges();
  };

  const cancelAccountStatusChange = () => {
    setShowAccountStatusChangeModal(false);
  };
  const confirmAccountStatusChange = () => {
    setShowAccountStatusChangeModal(false);
    saveChanges();
  };

  const confirmErrorModal = () => {
    setUserUpdateRequestStatus(types.requestStatuses.initial);
  };

  const handleResendInviteButtonClick = async () => {
    setResendUserInviteRequestStatus(types.requestStatuses.inProgress);

    try {
      await postUserInvite(user.usersId);
    } catch {
      setResendUserInviteRequestStatus(types.requestStatuses.failure);
    }

    dispatch(fetchUsers(currentPage));
    setResendUserInviteRequestStatus(types.requestStatuses.success);
    setShowInviteResentSuccessNotification(true);
    setInviteDateUtc(new Date().toISOString());
  };

  useEffect(() => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setUserRole(user.role);
    setPhoneNumber(user.phone);
    setAssociatedCarrierList(user.carrierList);
    setAccountEnabled(user.accountEnabled);
    setUserUpdateRequestStatus(types.requestStatuses.initial);
    setResendUserInviteRequestStatus(types.requestStatuses.initial);
  }, [user]);

  useEffect(() => {
    setAssociatedCarrierList(
      user.carrierList?.map((carrier) => ({
        id: carrier.carrierId,
        name: carrier.carrierName,
      }))
    );
  }, [user.carrierList]);

  useEffect(() => {
    setAllCarriers(
      carriers.map((carrier) => ({
        id: carrier.id,
        name: carrier.carrierName,
      }))
    );
  }, [carriers]);

  const userUpdateInProgress =
    userUpdateRequestStatus === types.requestStatuses.inProgress ||
    isFetchingUsers;
  const inviteResendInProgress =
    resendUserInviteRequestStatus === types.requestStatuses.inProgress;

  return (
    <>
      <Grid container spacing={2} direction="column" wrap="wrap">
        <Grid item xs={12}>
          <Typography variant="h4" style={{ fontWeight: "bold" }}>
            User Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <StyledTextField
                label="First Name"
                variant="outlined"
                value={firstName ?? ""}
                onChange={handleFirstNameChange}
                fullWidth={true}
                inputProps={{
                  "data-testid": "user-details-first-name-text-field",
                  maxLength: 128,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                label="Last Name"
                variant="outlined"
                value={lastName ?? ""}
                onChange={handleLastNameChange}
                fullWidth={true}
                inputProps={{
                  "data-testid": "user-details-last-name-text-field",
                  maxLength: 128,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                label="Phone #"
                variant="outlined"
                value={phoneNumber ?? ""}
                onChange={(phoneNumber) =>
                  handlePhoneNumberChange(phoneNumber, 1)
                }
                fullWidth={true}
                inputProps={{
                  "data-testid": "user-update-phone-number-text-field",
                  maxLength: 15,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              {user.role === types.goTrackUserRoles.shipper ||
              user.role === types.goTrackUserRoles.shipperManager ? (
                <StyledFormControl variant="outlined" fullWidth={true}>
                  <InputLabel id="user-type">User type</InputLabel>
                  <StyledSelect
                    value={userRole}
                    label="User type"
                    labelId="user-type"
                    onChange={handleSelectedUserType}
                    data-testid="user-details-role-select"
                  >
                    <MenuItem value={types.goTrackUserRoles.shipper}>
                      Shipper
                    </MenuItem>
                    <MenuItem value={types.goTrackUserRoles.shipperManager}>
                      Shipper Manager
                    </MenuItem>
                  </StyledSelect>
                </StyledFormControl>
              ) : (
                <StyledTextField
                  label="User Type"
                  variant="outlined"
                  value={types.goTrackUserRolesDescription[userRole] ?? ""}
                  fullWidth={true}
                  inputProps={{
                    "data-testid": "user-details-role-text-field",
                  }}
                  disabled={true}
                />
              )}
            </Grid>
            <Grid
              item
              xs={
                user.lastActiveDateUtc ||
                resendUserInviteRequestStatus === types.requestStatuses.success
                  ? 6
                  : 4
              }
            >
              Invited <br />
              {isAfter(parseISO(inviteDateUtc), new Date(2020, 1, 1))
                ? formatUserLocalTimeWithYear(inviteDateUtc)
                : "--"}
            </Grid>
            <Grid
              item
              xs={
                user.lastActiveDateUt ||
                resendUserInviteRequestStatus === types.requestStatuses.success
                  ? 6
                  : 4
              }
              style={{ marginBottom: "auto !important" }}
            >
              Last Active <br />
              {user.lastActiveDateUtc
                ? formatUserLocalTimeWithYear(user.lastActiveDateUtc)
                : "--"}
            </Grid>
            {!user.lastActiveDateUtc &&
              resendUserInviteRequestStatus !==
                types.requestStatuses.success && (
                <Grid
                  item
                  xs={4}
                  style={{
                    marginBottom: "auto !important",
                    textAlign: "right",
                  }}
                >
                  <Button
                    size="large"
                    disabled={inviteResendInProgress}
                    startIcon={
                      inviteResendInProgress ? (
                        <CircularProgress size={15} />
                      ) : undefined
                    }
                    onClick={handleResendInviteButtonClick}
                    data-testid="resend-invite-button"
                  >
                    RESEND INVITE
                  </Button>
                </Grid>
              )}
            <Grid item xs={6} style={{ marginBottom: "auto !important" }}>
              <StyledFormControl variant="outlined" fullWidth={true}>
                <InputLabel id="user-status">User status</InputLabel>
                <StyledSelect
                  value={accountEnabled}
                  label="User status"
                  labelId="user-status"
                  onChange={handleAccountEnabledChange}
                  data-testid="user-details-status-select"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Deactivated</MenuItem>
                </StyledSelect>
              </StyledFormControl>
            </Grid>
          </Grid>
        </Grid>
        {isCarrier([user.role]) && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h4" style={{ fontWeight: "bold" }}>
                Carrier Info
              </Typography>
              <AutoCompleteMultiSelect
                autoCompleteTestId="user-details-carrier-association-autocomplete"
                options={allCarriers}
                selectedOptions={associatedCarrierList}
                setSelectedOptions={handleAssociatedCarriersChange}
                label="Associate a carrier"
                chipsContainerStyles={{
                  overflowY: "auto",
                  maxHeight: "calc(100% - 450px)",
                  position: "absolute",
                }}
                error={isMissingCarrierAssociation}
                setError={setIsMissingCarrierAssociation}
                errorMessage={
                  "At least one carrier is required for this user type."
                }
                chipsDescription={"Associated carriers"}
                maxNumberOfSelectedOptions={30}
              />
            </Grid>
          </>
        )}
      </Grid>
      <StickyButton
        onClick={handleSaveButtonClick}
        disabled={userUpdateInProgress}
        startIcon={
          userUpdateInProgress ? <CircularProgress size={15} /> : undefined
        }
        data-testid="user-details-save-button"
        style={{
          marginBottom: "12px",
          width: "40%",
        }}
      >
        Save Changes
      </StickyButton>
      <ActionsModal
        handleClose={() => {}}
        open={showAccountStatusChangeModal}
        title={accountEnabled ? "Reactivate user?" : "Deactivate user?"}
        message={
          accountEnabled
            ? "If you reactivate this user, they will regain access to RouteConnect™. Are you sure you'd like to reactivate this user?"
            : "If you deactivate this user, they will no longer have access to RouteConnect™. Are you sure you'd like to deactivate this user?"
        }
        handleAffirmative={confirmAccountStatusChange}
        handleNegative={cancelAccountStatusChange}
        affirmativeButtonText={accountEnabled ? "Reactivate" : "Deactivate"}
        negativeButtonText="Cancel"
      />
      <ActionsModal
        handleClose={() => {}}
        open={userUpdateRequestStatus === types.requestStatuses.failure}
        title={"Error Processing Request"}
        message={
          "There was an error processing your request. Changes may not be saved."
        }
        handleAffirmative={confirmErrorModal}
        affirmativeButtonText={"OK"}
      />
    </>
  );
}

UserUpdateForm.propTypes = {
  setShowUserUpdateSuccessNotification: PropTypes.func.isRequired,
  setShowInviteResentSuccessNotification: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
};

export default UserUpdateForm;
